import { getImageAssetUrl } from '../../lib/api'

interface CanvasWorkspaceProps {
  aspectRatio: string
  /** When provided, shows the generated image instead of the placeholder grid. */
  imageId?: string | null
  imageUrl?: string | null
  /** Optional label shown below the canvas */
  label?: string
  className?: string
}

/**
 * Parse "W:H" aspect ratio string to a CSS padding-bottom percentage.
 * Used to create a proportional box before the image is generated.
 */
function ratioToPaddingBottom(aspectRatio: string): string {
  const [wStr, hStr] = aspectRatio.split(':')
  const w = parseFloat(wStr ?? '1')
  const h = parseFloat(hStr ?? '1')
  if (!w || !h || isNaN(w) || isNaN(h)) return '100%'
  return `${((h / w) * 100).toFixed(2)}%`
}

export function CanvasWorkspace({
  aspectRatio,
  imageId,
  imageUrl,
  label,
  className = '',
}: CanvasWorkspaceProps) {
  const paddingBottom = ratioToPaddingBottom(aspectRatio)
  const hasImage = !!(imageId || imageUrl)
  const src = imageId ? getImageAssetUrl(imageId) : imageUrl

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      <div className="w-full relative" style={{ paddingBottom }}>
        <div className="absolute inset-0 rounded-lg overflow-hidden border border-slate-200 shadow-sm">
          {hasImage && src ? (
            <img
              src={src}
              alt="Canvas preview"
              className="w-full h-full object-contain bg-slate-50"
              onError={(e) => {
                if (imageId && imageUrl) {
                  ;(e.target as HTMLImageElement).src = imageUrl
                }
              }}
            />
          ) : (
            /* Checkerboard placeholder — signals an empty canvas */
            <div
              className="w-full h-full flex flex-col items-center justify-center"
              style={{
                backgroundImage:
                  'repeating-conic-gradient(#e2e8f0 0% 25%, #f8fafc 0% 50%)',
                backgroundSize: '20px 20px',
              }}
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-xs font-semibold text-slate-600">{aspectRatio}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">Canvas vacio</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {label && (
        <p className="text-xs text-slate-500 text-center">{label}</p>
      )}
    </div>
  )
}
